import { getSessionStrict, type BdfzSession } from "./identity";
import { publishCompletionStage, type LearningUnitCompletion, type DeliveryStage } from "./learningEvidence";

export interface SyncState {
  phase: "idle" | "syncing" | "synced" | "anonymous" | "error";
  pending: number;
  retryable: boolean;
  accountChanged?: boolean;
  storageFailed?: boolean;
  exhausted?: boolean;
  expired?: number;
  quarantined?: number;
  pendingMapping?: number;
}
const PREFIX = "ly:completion-outbox:v1:";
const TTL = 7 * 86400000;
const MAX_ITEMS = 200;
const MAX_ATTEMPTS = 5;
// Raised only by a release that fixes a server-side delivery failure: each queued entry that exhausted its budget under
// an older generation gets exactly one fresh budget. Confirmed stages are kept, so nothing already accepted is resent.
const DELIVERY_GENERATION = 3;
const STAGES: DeliveryStage[] = ["source", "progress", "record"];
type Disposition = "queued" | "accepted" | "pending_mapping" | "quarantined";
type StageDisposition = "accepted" | "pending_mapping";
interface Entry { completion: LearningUnitCompletion; owner: string; stage: number; attempts: number; nextAt: number; expiresAt: number;
  disposition?: Disposition; reason?: string; stageDispositions?: Partial<Record<DeliveryStage, StageDisposition>>;
  generation?: number; lastFailure?: string }
type Store = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;
// my.bdfz.net /api/session identifies the account by slug only (formatUser returns no numeric id);
// a numeric id, when present, is preferred. The same bound as functions/api/learning/publish.js.
const OWNER = /^(?:[1-9]\d{0,15}|[A-Za-z0-9][A-Za-z0-9_.-]{0,79})$/;
const ownerOf = (s: BdfzSession) => {
  if (!s.authenticated) return "";
  const key = s.user?.id ? String(s.user.id) : String(s.user?.slug ?? "");
  return OWNER.test(key) ? key : "";
};
const keyOf = (e: Entry) => `${PREFIX}${e.owner}:${e.completion.eventNonce}`;
// The same bound as functions/api/learning/publish.js: a nonce the proxy would refuse never enters the outbox.
const NONCE = /^[A-Za-z0-9:_-]{20,100}$/;
function validStageDispositions(value: unknown): boolean {
  if (value === undefined) return true;
  if (!value || typeof value !== "object") return false;
  return Object.entries(value as Record<string, unknown>).every(([stage, disposition]) =>
    (STAGES as string[]).includes(stage) && (disposition === "accepted" || disposition === "pending_mapping"));
}
function valid(e: Entry): boolean {
  const c = e?.completion;
  return !!c && OWNER.test(e.owner) && NONCE.test(c.eventNonce)
    && c.resourceKey === `ly:q${String(c.quoteId).padStart(4, "0")}:${c.gate}`
    && ["meaning", "misread", "context", "recall"].includes(c.gate)
    && Number.isInteger(c.quoteId) && c.quoteId > 0 && c.quoteId <= 541 && typeof c.correct === "boolean"
    && Number.isSafeInteger(c.attemptNumber) && c.attemptNumber > 0 && Number.isSafeInteger(c.submittedAt)
    && Number.isInteger(e.stage) && e.stage >= 0 && e.stage <= 3
    && Number.isInteger(e.attempts) && e.attempts >= 0 && e.attempts <= MAX_ATTEMPTS
    && Number.isFinite(e.nextAt) && e.expiresAt === c.submittedAt + TTL
    && (e.disposition === undefined || ["queued", "accepted", "pending_mapping", "quarantined"].includes(e.disposition))
    && validStageDispositions(e.stageDispositions)
    && (e.generation === undefined || (Number.isInteger(e.generation) && e.generation >= 1 && e.generation <= DELIVERY_GENERATION))
    && (e.lastFailure === undefined || (typeof e.lastFailure === "string" && /^[a-z_]{1,24}$/.test(e.lastFailure)));
}
const heldCentrally = (e: Entry) => e.disposition === "pending_mapping";
export function createLearningPublisher(onChange: (value: SyncState) => void, dependencies: {
  session?: () => Promise<BdfzSession>;
  publishStage?: typeof publishCompletionStage;
  storage?: Store;
  now?: () => number;
} = {}) {
  const session = dependencies.session ?? getSessionStrict;
  const publish = dependencies.publishStage ?? publishCompletionStage;
  const now = dependencies.now ?? Date.now;
  let store: Store | undefined;
  try { store = dependencies.storage ?? globalThis.localStorage; } catch { /* Report below, never claim persistence. */ }
  let running: Promise<void> | null = null;
  let stopped = false, storageFailed = false;
  let currentOwner = "";
  const malformed = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let bindingChain = Promise.resolve();
  // An event nonce counts as seen only once its entry is durably stored; a failed enqueue may be resubmitted.
  const seen = new Set<string>();
  const pending = new Map<string, Entry>();
  const ownedBy = (owner: string) => [...pending.values()].filter(e => e.owner === owner);
  const change = (state: SyncState) => {
    const owned = ownedBy(currentOwner);
    const quarantined = owned.filter(e => e.disposition === "quarantined").length
      + [...malformed].filter(key => key.startsWith(`${PREFIX}${currentOwner}:`)).length;
    if (!stopped) onChange({ storageFailed, ...state, expired: owned.filter(e => e.reason === "expired").length,
      quarantined, pendingMapping: owned.filter(heldCentrally).length });
  };
  function save(e: Entry) {
    if (!store) throw new Error("storage_unavailable");
    store.setItem(keyOf(e), JSON.stringify(e));
    storageFailed = false;
  }
  function load() {
    if (!store) { storageFailed = true; return; }
    try {
      const keys: string[] = [];
      for (let i = 0; i < store.length; i++) { const key = store.key(i); if (key?.startsWith(PREFIX)) keys.push(key); }
      for (const key of keys) {
        let e: Entry;
        try { e = JSON.parse(store.getItem(key) || "null"); } catch { malformed.add(key); pending.delete(key); continue; }
        if (!valid(e) || keyOf(e) !== key) { malformed.add(key); pending.delete(key); continue; }
        malformed.delete(key);
        // Only an unconfirmed stage expires. A stage the centre already holds (pending_mapping) is never expired here.
        if (e.stage < STAGES.length && e.expiresAt <= now() && e.disposition !== "quarantined") {
          e.disposition = "quarantined"; e.reason = "expired"; save(e);
        }
        if (e.stage < STAGES.length && e.attempts >= MAX_ATTEMPTS && (e.disposition ?? "queued") === "queued"
          && (e.generation ?? 1) < DELIVERY_GENERATION) {
          e.attempts = 0; e.nextAt = 0; e.generation = DELIVERY_GENERATION; save(e);
        }
        // Merge independently stored entries; another tab cannot erase this queue.
        pending.set(key, e);
      }
    } catch { storageFailed = true; }
  }
  async function verifiedSession() {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try { return await Promise.race([session(), new Promise<BdfzSession>((_, reject) => {
      timeout = setTimeout(() => reject(new Error("session_unconfirmed")), 12000);
    })]); } finally { clearTimeout(timeout); }
  }
  async function verifyOwner(owner: string) {
    if (ownerOf(await verifiedSession()) !== owner) {
      currentOwner = "";
      throw new Error("session_changed");
    }
  }
  function close(entry: Entry) {
    entry.disposition = "accepted"; save(entry);
    store!.removeItem(keyOf(entry)); pending.delete(keyOf(entry));
  }
  async function drain() {
    clearTimeout(timer); timer = undefined;
    load();
    let owner: string;
    try { owner = ownerOf(await verifiedSession()); } catch {
      change({ phase: "error", pending: pending.size, retryable: false }); return;
    }
    if (stopped) return;
    currentOwner = owner;
    const entries = ownedBy(owner).sort((a, b) => a.completion.submittedAt - b.completion.submittedAt);
    const malformedCount = [...malformed].filter(key => key.startsWith(`${PREFIX}${owner}:`)).length;
    if (!entries.length) {
      change({ phase: pending.size || malformedCount ? "error" : owner ? "idle" : "anonymous", pending: malformedCount,
        retryable: false, accountChanged: pending.size > 0 }); return;
    }
    // One stuck original event never blocks the others: terminal, exhausted and backing-off entries are skipped and
    // reported; every ready entry of this owner is attempted in submission order.
    let earliestNext = Infinity, exhausted = false, accountChanged = false;
    for (const entry of entries) {
      if (stopped) return;
      if (entry.disposition === "quarantined" || heldCentrally(entry)) continue;
      if (entry.stage >= STAGES.length) { // every stage already confirmed; nothing is sent
        try { close(entry); } catch { storageFailed = true; break; }
        continue;
      }
      if (entry.attempts >= MAX_ATTEMPTS) { exhausted = true; continue; }
      if (entry.nextAt > now()) { earliestNext = Math.min(earliestNext, entry.nextAt); continue; }
      try {
        await verifyOwner(owner);
        if (stopped) return;
        entry.attempts++; entry.nextAt = now() + Math.min(60000, 2000 * 2 ** (entry.attempts - 1));
        save(entry); // Persist the attempt BEFORE the uncertain write; refresh cannot reset its budget.
        change({ phase: "syncing", pending: entries.length, retryable: false });
        while (entry.stage < STAGES.length) {
          if (stopped) return;
          await verifyOwner(owner);
          const stage = STAGES[entry.stage];
          try {
            await publish(entry.completion, owner, stage);
            entry.stageDispositions = { ...entry.stageDispositions, [stage]: "accepted" };
          } catch (error) {
            const failure = error as { disposition?: string } | null;
            if (failure?.disposition !== "pending_mapping") throw error;
            // The centre durably holds this stage while its learning-item mapping is pending: it is neither resent
            // nor reported as synced or scored.
            entry.stageDispositions = { ...entry.stageDispositions, [stage]: "pending_mapping" };
          }
          entry.stage++; save(entry); // A confirmed earlier stage is never cleared by a later failure.
          // Save the original owner's ACK, then fence every later write and UI update.
          if (stopped) return;
          await verifyOwner(owner);
        }
        if (Object.values(entry.stageDispositions ?? {}).includes("pending_mapping")) {
          entry.disposition = "pending_mapping"; entry.reason = "central_mapping_pending"; save(entry);
        } else close(entry);
      } catch (error) {
        const changed = error instanceof Error && error.message === "session_changed";
        const failure = error as { retryable?: boolean; disposition?: string; step?: string } | null;
        if (!changed && failure?.step) entry.lastFailure = failure.step;
        if (!changed && (failure?.disposition === "quarantined" || failure?.retryable === false)) {
          entry.disposition = "quarantined";
          entry.reason = error instanceof Error ? error.message : "delivery_rejected";
        }
        try { save(entry); } catch { storageFailed = true; }
        if (changed) { accountChanged = true; break; }
        if (storageFailed) break;
        if (entry.disposition === "quarantined") continue;
        if (entry.attempts >= MAX_ATTEMPTS) { exhausted = true; continue; }
        earliestNext = Math.min(earliestNext, entry.nextAt);
      }
    }
    const remaining = ownedBy(owner).length + malformedCount;
    if (accountChanged) { change({ phase: "error", pending: remaining, retryable: false, accountChanged: true }); return; }
    if (storageFailed) { change({ phase: "error", pending: remaining, retryable: false }); return; }
    if (!remaining) { change({ phase: "synced", pending: 0, retryable: false }); return; }
    const retryable = Number.isFinite(earliestNext);
    change({ phase: "error", pending: remaining, retryable, exhausted });
    if (retryable && !stopped) timer = setTimeout(() => { void run(); }, Math.max(1, earliestNext - now()));
  }
  function run() {
    if (stopped) return Promise.resolve();
    if (!running) running = drain().finally(() => { running = null; });
    return running;
  }
  return {
    start() { return run(); },
    submit(completion: LearningUnitCompletion) {
      if (seen.has(completion.eventNonce)) return bindingChain;
      // Bind the answer to the account confirmed at submission time, in submission order. A session confirmed later
      // (a different login) can never claim it; the drain re-verifies the same owner before every write.
      bindingChain = bindingChain.then(async () => {
        if (stopped || seen.has(completion.eventNonce)) return;
        let owner = currentOwner;
        if (!owner) {
          try { owner = ownerOf(await verifiedSession()); } catch {
            change({ phase: "error", pending: pending.size, retryable: false }); return;
          }
          if (stopped) return;
          currentOwner = owner;
        }
        if (!owner) { change({ phase: "anonymous", pending: 0, retryable: false }); return; }
        load();
        const entry: Entry = { completion: { resourceKey: completion.resourceKey, quoteId: completion.quoteId,
          gate: completion.gate, correct: completion.correct, attemptNumber: completion.attemptNumber,
          submittedAt: completion.submittedAt, eventNonce: completion.eventNonce },
          owner, stage: 0, attempts: 0, nextAt: 0, expiresAt: completion.submittedAt + TTL, disposition: "queued",
          generation: DELIVERY_GENERATION };
        if (pending.has(keyOf(entry))) { seen.add(completion.eventNonce); return; }
        if (!valid(entry) || entry.expiresAt <= now() || ownedBy(owner).length >= MAX_ITEMS) {
          change({ phase: "error", pending: pending.size, retryable: false }); return;
        }
        try { save(entry); } catch { storageFailed = true; change({ phase: "error", pending: pending.size, retryable: false }); return; }
        pending.set(keyOf(entry), entry);
        seen.add(completion.eventNonce);
        await run();
      });
      return bindingChain;
    },
    retry() { return bindingChain.then(run); },
    stop() { stopped = true; clearTimeout(timer); },
  };
}
